/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type UserUpdateProfileRequest } from '@trebox-studio/api-schema/passport';
import { passport } from '@trebox-studio/api-schema';
import { resetUserStore, type UserInfo } from '@trebox-foundation/account-base';

import { apiJson } from '../trebox-session';

// trebox session-restore: hit the native /api/auth/me (the legacy coze
// /passport/account/info/v2 path is a 404 on trebox, which made every
// authenticated page bounce to /sign on a hard refresh — and broke tenant
// switching, which reloads). apiJson injects the bearer and transparently
// refreshes on a 401, so an expired access token self-heals instead of forcing
// a logout. The response is mapped onto the UserInfo shape useLoginStatus
// expects (user_id_str).
interface AuthMeResponse {
  principal?: { id?: string; display_name?: string };
  identifiers?: Array<{ type: string; value: string; is_primary?: boolean }>;
}

export const passportApi = {
  checkLogin: async (): Promise<UserInfo> => {
    const body = await apiJson<AuthMeResponse>('/api/auth/me');
    const principalId = body.principal?.id ?? '';
    const email = body.identifiers?.find(i => i.type === 'email')?.value ?? '';
    const name =
      body.principal?.display_name ||
      body.identifiers?.find(i => i.is_primary)?.value ||
      body.identifiers?.[0]?.value ||
      principalId;
    return {
      user_id_str: principalId,
      user_id: 0,
      name,
      screen_name: name,
      email,
      email_collected: !!email,
    } as unknown as UserInfo;
  },

  logout: async () => {
    await passport.PassportWebLogoutGet({
      next: '/',
    });
  },

  uploadAvatar: async ({ avatar }: { avatar: File }) => {
    const res = await passport.UserUpdateAvatar({
      avatar,
    });

    return res.data;
  },

  updatePassword: async (params: { password: string; email: string }) => {
    await passport.PassportWebEmailPasswordResetGet({ ...params, code: '' });
    // After updating the password, the current login state is invalid, reset the store
    resetUserStore();
  },

  updateUserProfile: (params: UserUpdateProfileRequest) =>
    passport.UserUpdateProfile(params),
};
